import { supabase } from '../lib/supabase';

export const attorneysService = {
  // Get all attorneys with their related data
  async getAllAttorneys() {
    try {
      const { data, error } = await supabase?.from('attorneys')?.select(`
          *,
          attorney_education:attorney_education(*),
          attorney_admissions:attorney_admissions(*),
          attorney_achievements:attorney_achievements(*),
          attorney_specializations:attorney_specializations(
            *,
            practice_areas:practice_areas(*)
          )
        `)?.order('display_order', { ascending: true });

      if (error) {
        throw error;
      }

      // Transform the data to match the frontend structure
      const transformedData = data?.map(attorney => ({
        ...attorney,
        image: attorney?.image_url, // Map image_url to image for frontend compatibility
        specializations: attorney?.attorney_specializations?.map(spec => 
          spec?.practice_areas?.title
        )?.filter(Boolean) || [],
        education: attorney?.attorney_education?.map(edu => 
          `${edu.degree || 'Degree'} from ${edu.school}`
        ) || ['Law School'],
        professional_membership: attorney?.professional_membership || ''
      }));

      return { data: transformedData, error: null };
    } catch (error) {
      console.log('Error fetching attorneys:', error);
      return { data: null, error: error?.message };
    }
  },

  // Get attorney by ID
  async getAttorneyById(id) {
    try {
      const { data, error } = await supabase?.from('attorneys')?.select(`
          *,
          attorney_education:attorney_education(*),
          attorney_admissions:attorney_admissions(*),
          attorney_achievements:attorney_achievements(*),
          attorney_specializations:attorney_specializations(
            *,
            practice_areas:practice_areas(*)
          ),
          publications:publications(*)
        `)?.eq('id', id)?.single();

      if (error) {
        throw error;
      }

      // Transform the data to match the frontend structure
      const transformedData = {
        ...data,
        image: data?.image_url, // Map image_url to image for frontend compatibility
        specializations: data?.attorney_specializations?.map(spec => 
          spec?.practice_areas?.title
        )?.filter(Boolean) || [],
        education: data?.attorney_education?.map(edu => 
          `${edu.degree || 'Degree'} from ${edu.school}`
        ) || ['Law School'],
        professional_membership: data?.professional_membership || ''
      };

      return { data: transformedData, error: null };
    } catch (error) {
      console.log('Error fetching attorney:', error);
      return { data: null, error: error?.message };
    }
  },

  // Get featured attorneys
  async getFeaturedAttorneys() {
    try {
      const { data, error } = await supabase?.from('attorneys')?.select(`
          *,
          attorney_specializations:attorney_specializations(
            *,
            practice_areas:practice_areas(*)
          )
        `)?.eq('is_featured', true)?.order('display_order', { ascending: true });

      if (error) {
        throw error;
      }

      // Transform the data
      const transformedData = data?.map(attorney => ({
        ...attorney,
        image: attorney?.image_url, // Map image_url to image for frontend compatibility
        specializations: attorney?.attorney_specializations?.map(spec => 
          spec?.practice_areas?.title
        )?.filter(Boolean) || []
      }));

      return { data: transformedData, error: null };
    } catch (error) {
      console.log('Error fetching featured attorneys:', error);
      return { data: null, error: error?.message };
    }
  },

  // Get attorneys by practice area
  async getAttorneysByPracticeArea(practiceAreaId) {
    try {
      const { data, error } = await supabase?.from('attorney_specializations')?.select(`
          attorneys:attorneys(
            *,
            attorney_education:attorney_education(*),
            attorney_specializations:attorney_specializations(
              *,
              practice_areas:practice_areas(*)
            )
          )
        `)?.eq('practice_area_id', practiceAreaId);

      if (error) {
        throw error;
      }

      // Extract and transform attorney data
      const attorneys = data?.map(item => item?.attorneys)?.filter(Boolean);
      const transformedData = attorneys?.map(attorney => ({
        ...attorney,
        specializations: attorney?.attorney_specializations?.map(spec => 
          spec?.practice_areas?.title
        )?.filter(Boolean) || []
      }));

      return { data: transformedData, error: null };
    } catch (error) {
      console.log('Error fetching attorneys by practice area:', error);
      return { data: null, error: error?.message };
    }
  },

  // Search attorneys
  async searchAttorneys(searchTerm) {
    try {
      const { data, error } = await supabase?.from('attorneys')?.select(`
          *,
          attorney_specializations:attorney_specializations(
            *,
            practice_areas:practice_areas(*)
          )
        `)?.or(`name.ilike.%${searchTerm}%,title.ilike.%${searchTerm}%,bio.ilike.%${searchTerm}%`)?.order('display_order', { ascending: true });

      if (error) {
        throw error;
      }

      // Transform the data
      const transformedData = data?.map(attorney => ({
        ...attorney,
        specializations: attorney?.attorney_specializations?.map(spec => 
          spec?.practice_areas?.title
        )?.filter(Boolean) || []
      }));

      return { data: transformedData, error: null };
    } catch (error) {
      console.log('Error searching attorneys:', error);
      return { data: null, error: error?.message };
    }
  },

  // Create a new attorney
  async createAttorney(attorneyData) {
    try {
      const { data, error } = await supabase
        .from('attorneys')
        .insert([attorneyData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.log('Error creating attorney:', error);
      return { data: null, error: error?.message };
    }
  },

  // Update an attorney
  async updateAttorney(id, updates) {
    try {
      const { data, error } = await supabase
        .from('attorneys')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.log('Error updating attorney:', error);
      return { data: null, error: error?.message };
    }
  },

  // Delete an attorney
  async deleteAttorney(id) {
    try {
      const { error } = await supabase
        .from('attorneys')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      return { error: null };
    } catch (error) {
      console.log('Error deleting attorney:', error);
      return { error: error?.message };
    }
  },

  // Manage attorney education
  async getAttorneyEducation(attorneyId) {
    try {
      const { data, error } = await supabase
        .from('attorney_education')
        .select('*')
        .eq('attorney_id', attorneyId)
        .order('year', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.log('Error fetching attorney education:', error);
      return { data: null, error: error?.message };
    }
  },

  async addAttorneyEducation(attorneyId, educationData) {
    try {
      const { data, error } = await supabase
        .from('attorney_education')
        .insert([{ ...educationData, attorney_id: attorneyId }])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.log('Error adding attorney education:', error);
      return { data: null, error: error?.message };
    }
  },

  async updateAttorneyEducation(educationId, updates) {
    try {
      const { data, error } = await supabase
        .from('attorney_education')
        .update(updates)
        .eq('id', educationId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.log('Error updating attorney education:', error);
      return { data: null, error: error?.message };
    }
  },

  async deleteAttorneyEducation(educationId) {
    try {
      const { error } = await supabase
        .from('attorney_education')
        .delete()
        .eq('id', educationId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.log('Error deleting attorney education:', error);
      return { error: error?.message };
    }
  },

  // Manage attorney practice area specializations
  async getAttorneySpecializations(attorneyId) {
    try {
      const { data, error } = await supabase
        .from('attorney_specializations')
        .select(`
          *,
          practice_areas:practice_areas(*)
        `)
        .eq('attorney_id', attorneyId);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.log('Error fetching attorney specializations:', error);
      return { data: null, error: error?.message };
    }
  },

  async addAttorneySpecialization(attorneyId, practiceAreaId) {
    try {
      const { data, error } = await supabase
        .from('attorney_specializations')
        .insert([{ attorney_id: attorneyId, practice_area_id: practiceAreaId }])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.log('Error adding attorney specialization:', error);
      return { data: null, error: error?.message };
    }
  },

  async deleteAttorneySpecialization(specializationId) {
    try {
      const { error } = await supabase
        .from('attorney_specializations')
        .delete()
        .eq('id', specializationId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.log('Error deleting attorney specialization:', error);
      return { error: error?.message };
    }
  },

  // Manage attorney admissions
  async getAttorneyAdmissions(attorneyId) {
    try {
      const { data, error } = await supabase
        .from('attorney_admissions')
        .select('*')
        .eq('attorney_id', attorneyId)
        .order('admission_year', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.log('Error fetching attorney admissions:', error);
      return { data: null, error: error?.message };
    }
  },

  async addAttorneyAdmission(attorneyId, admissionData) {
    try {
      const { data, error } = await supabase
        .from('attorney_admissions')
        .insert([{ ...admissionData, attorney_id: attorneyId }])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.log('Error adding attorney admission:', error);
      return { data: null, error: error?.message };
    }
  },

  async updateAttorneyAdmission(admissionId, updates) {
    try {
      const { data, error } = await supabase
        .from('attorney_admissions')
        .update(updates)
        .eq('id', admissionId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.log('Error updating attorney admission:', error);
      return { data: null, error: error?.message };
    }
  },

  async deleteAttorneyAdmission(admissionId) {
    try {
      const { error } = await supabase
        .from('attorney_admissions')
        .delete()
        .eq('id', admissionId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.log('Error deleting attorney admission:', error);
      return { error: error?.message };
    }
  }
};